<script>
import { mapGetters } from 'vuex';
import MentionBox from '../mentions/MentionBox.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

export default {
  components: {
    MentionBox,
    FluentIcon,
  },
  props: {
    searchKey: {
      type: String,
      default: '',
    },
  },
  emits: ['replace'],
  computed: {
    ...mapGetters({
      cannedMessages: 'getCannedResponses',
    }),
    items() {
      const items = this.cannedMessages.map(cannedMessage => {
        const item = {
          label: cannedMessage.short_code,
          key: cannedMessage.short_code,
          content: cannedMessage.content || '',
          description: cannedMessage.content || '',
          attachments: cannedMessage.attachments || [],
          hasAttachments:
            cannedMessage.attachments && cannedMessage.attachments.length > 0,
        };
        return item;
      });

      return items;
    },
  },
  watch: {
    searchKey() {
      this.fetchCannedResponses();
    },
  },
  mounted() {
    this.fetchCannedResponses();
  },
  methods: {
    fetchCannedResponses() {
      this.$store.dispatch('getCannedResponse', { searchKey: this.searchKey });
    },
    handleMentionClick(item = {}) {
      this.$emit('replace', {
        content: item.content || item.description || '',
        attachments: item.attachments || [],
      });
    },
    getFileIcon(fileType) {
      if (fileType === 'image') return 'image';
      if (fileType === 'video') return 'video';
      if (fileType === 'audio') return 'audio';
      return 'document';
    },
    formatFileSize(bytes) {
      if (!bytes) return '';
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / k ** i).toFixed(2)) + ' ' + sizes[i];
    },
    isImageAttachment(attachment) {
      return attachment.file_type === 'image' && attachment.thumb_url;
    },
  },
};
</script>

<template>
  <MentionBox
    :items="items"
    :search-key="searchKey"
    :loading="false"
    :show-loading="false"
    @mentionSelect="handleMentionClick"
  >
    <template #item="{ item }">
      <div class="canned-response-item">
        <div class="canned-response-header">
          <span class="canned-response-code">{{ item.label }}</span>
          <span v-if="item.hasAttachments" class="attachment-indicator">
            <FluentIcon icon="attach" class="attachment-icon" />
            {{ item.attachments.length }}
          </span>
        </div>
        <div class="canned-response-content">
          <div v-if="item.description" class="content-text">
            {{ item.description }}
          </div>

          <!-- Attachments Preview -->
          <div v-if="item.hasAttachments" class="attachments-preview">
            <div
              v-for="(attachment, index) in item.attachments.slice(0, 3)"
              :key="index"
              class="attachment-preview-item"
            >
              <!-- Image Preview -->
              <img
                v-if="isImageAttachment(attachment)"
                :src="attachment.thumb_url"
                :alt="attachment.filename || $t('COMMON.IMAGE')"
                class="attachment-thumbnail"
              />
              <!-- File Icon -->
              <div v-else class="attachment-file-icon">
                <FluentIcon :icon="getFileIcon(attachment.file_type)" />
                <span class="file-name">
                  {{ attachment.filename || $t('COMMON.FILE') }}
                </span>
                <span class="file-size">
                  {{ formatFileSize(attachment.file_size) }}
                </span>
              </div>
            </div>

            <!-- More files indicator -->
            <div
              v-if="item.attachments.length > 3"
              class="more-files-indicator"
            >
              +{{ item.attachments.length - 3 }}
              {{ $t('COMMON.MORE') }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </MentionBox>
</template>

<style lang="scss" scoped>
.canned-response-item {
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--s-50);
  }
}

.canned-response-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.canned-response-code {
  font-weight: 600;
  color: var(--s-900);
  font-size: 14px;
}

.attachment-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--s-600);
}

.attachment-icon {
  width: 12px;
  height: 12px;
}

.canned-response-content {
  .content-text {
    color: var(--s-700);
    font-size: 13px;
    line-height: 1.4;
    margin-bottom: 8px;
  }
}

.attachments-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.attachment-preview-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background-color: var(--s-100);
  border-radius: 4px;
  font-size: 12px;
}

.attachment-thumbnail {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
}

.attachment-file-icon {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--s-600);

  .file-name {
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-size {
    color: var(--s-500);
    font-size: 11px;
  }
}

.more-files-indicator {
  font-size: 11px;
  color: var(--s-500);
  font-style: italic;
}
</style>
